import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Globe, AlertCircle } from 'lucide-react';
import { LandingHeader } from '@/components/layout/landing-header';
import { useAuth } from '@/context/auth-context';
import { authFetch } from '@/utils/auth-fetch';
import { toast } from '@/components/ui/use-toast';

const WaitlistAddSitePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateUrl = (inputUrl: string): boolean => {
    try {
      const urlToValidate = inputUrl.startsWith('http') 
        ? inputUrl 
        : `https://${inputUrl}`;
      const parsed = new URL(urlToValidate);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!url.trim()) {
      setError('Please enter your website URL');
      return;
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid website URL');
      return;
    }

    setLoading(true);

    try {
      // Format URL properly
      const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
      
      // Submit to waitlist API
      const response = await authFetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user?.email,
          name: user?.name,
          website_url: formattedUrl
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to join waitlist');
      }

      // Success - navigate to confirmation
      navigate('/waitlist-confirmation');
    } catch (err) {
      console.error('Error joining waitlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to join waitlist. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <LandingHeader showBackButton={true} currentPage="waitlist" />
      
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Globe className="h-8 w-8 text-blue-600" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Tell us about your website
          </h1>
          
          <p className="text-lg text-gray-600">
            We'll analyze your site and prepare a personalized demo for you
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="website" className="text-base font-medium mb-2">
                Your Website URL
              </Label>
              <Input
                id="website"
                type="text"
                placeholder="www.example.com"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError('');
                }}
                className="h-12 text-base"
                disabled={loading}
              />
              <p className="text-sm text-gray-500 mt-2">
                Enter the main URL of your website
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="pt-4">
              <Button 
                type="submit"
                size="lg" 
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Analyzing your site...
                  </>
                ) : (
                  <>
                    Join Waitlist
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              We'll crawl your site to understand your content and prepare
              <br />
              a customized AI assistant just for you
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WaitlistAddSitePage;